import { useConfigurationStore, useDeviceStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useLoading } from '../useLoading'
import { BaseDevice, type Role, ConfigCapability, RoleId } from '@rapoo/device-sdk'

export function useAdminMode() {
  const deviceStore = useDeviceStore()
  const { currentDevice, expandedDevices } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()

  const { isLoading, withLoading } = useLoading()

  // 设备支持管理员调整出厂设置
  const adminFactory = (device: BaseDevice) => {
    return device.manifest?.capabilities.find(
      (item): item is ConfigCapability => item.type === 'config',
    )?.config?.adminFactory
  }

  /**
   * 只有声明了角色槽位的设备，登录态切换前后的 cfgSel 才属于不同的索引视角。
   * 普通零售设备没有 configSlots，继续保留原有的上报校正逻辑。
   */
  const refreshProfilesAfterRoleChange = () => {
    const config = currentDevice.value?.manifest.capabilities.find(
      (item): item is ConfigCapability => item.type === 'config',
    )
    const hasRoleScopedSlots = !!config?.config.configSlots?.length
    return configStore.refreshProfiles(hasRoleScopedSlots ? { reconcileReport: false } : undefined)
  }

  const adminLogin = () => {
    withLoading(async () => {
      try {
        const userStore = useUserStore()
        const roles = (userStore.userInfo?.roleIds ?? []) as Role[]
        // 先更新所有设备的配置角色（expandedDevices 已包含子设备）
        for (const device of expandedDevices.value) {
          if (device.original.features.config) {
            device.original.setConfigSlotRoles(roles)
          }
        }
        for (const device of expandedDevices.value) {
          const config = device.original.features.config
          if (!config) continue
          if (!adminFactory(device.original)) continue
          // 1. 恢复设备内管理员设置的出厂设置
          await config.initAllConfigsByAdmin()
          // 2. 进入管理员模式
          await enterAdminMode(device.original)
          // 3. 切换到管理员可见配置（如已在则跳过）
          await configStore.switchToRoleProfile(device.original, 'admin')
        }
        await refreshProfilesAfterRoleChange()
      } catch (error) {
        console.error(error)
      }
    })
  }

  const enterAdminMode = async (device: BaseDevice) => {
    const config = device.features.config
    if (!config) return
    if (!adminFactory(device)) return
    console.log(`[${device.manifest.model}] enter admin mode`)
    await config.enterAdminMode()
  }

  const adminLogout = () => {
    return withLoading(async () => {
      try {
        const roles: Role[] = [RoleId.user]
        // 退出后统一恢复普通用户槽位视角；空角色在 SDK 中表示不过滤全部槽位。
        for (const device of expandedDevices.value) {
          if (device.original.features.config) {
            device.original.setConfigSlotRoles(roles)
          }
        }
        for (const device of expandedDevices.value) {
          const config = device.original.features.config
          if (!config) continue
          if (!adminFactory(device.original)) continue
          // 1. 先退出管理员模式
          await config.exitAdminMode()
          device.original.adminMode = false
          // 2. 退出后切换到用户可见的第一个配置
          await configStore.switchToRoleProfile(device.original, 'user')
        }
        await refreshProfilesAfterRoleChange()
      } catch (error) {
        console.error(error)
      }
    })
  }

  return {
    adminLogin,
    adminLogout,
    enterAdminMode,
    isLoading,
  }
}
